/**
 * MemoryPanel — what the specialists remember about the active asset (SPEC §5, §8): each item with
 * its source (seed, case, user, voice), editable inline, deletable with a confirm step, and an add
 * box. The item named by `flash` (a memory.* event, e.g. "remember this site runs two shifts")
 * highlights for 2 s.
 */

import { useEffect, useState } from 'react';
import { X, Plus, Pencil, Trash2, Check } from 'lucide-react';
import { clockOf } from './agentUi.js';
import './MemoryPanel.css';

const FLASH_MS = 2000;
const SOURCE_LABEL = { seed: 'Seed', case: 'Case', user: 'User', voice: 'Voice' };
const KIND_LABEL = { fact: 'Fact', case_outcome: 'Case outcome', note: 'Note', open_item: 'Open item' };

function sourceText(source) {
  if (!source) return 'Unknown';
  const base = SOURCE_LABEL[source.kind] ?? source.kind;
  if (source.kind === 'case') return source.ref || source.case_id ? `${base} ${source.ref || source.case_id}` : base;
  if (source.kind === 'user' && source.user) return `${base} ${source.user}`;
  return base;
}

function MemoryItem({ item, flashing, readOnly, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setText(item.text); }, [item.text, editing]);

  const save = async () => {
    const t = text.trim();
    if (!t || t === item.text) { setEditing(false); return; }
    setBusy(true);
    try { await onEdit?.(item.id, t); setEditing(false); } catch { /* the error toast comes from the caller */ } finally { setBusy(false); }
  };

  return (
    <li className={`mem__item${flashing ? ' is-flash' : ''}${item.kind === 'open_item' && item.status === 'open' ? ' is-open-item' : ''}`}>
      <div className="mem__chips">
        <span className="mem__chip">{KIND_LABEL[item.kind] ?? item.kind}{item.kind === 'open_item' && item.status ? ` · ${item.status}` : ''}</span>
        <span className="mem__chip is-source">{sourceText(item.source)}</span>
        {item.scope && item.scope !== 'asset' && <span className="mem__chip">{item.scope}</span>}
        <span className="mem__time">{clockOf(item.created_at)}</span>
      </div>
      {editing ? (
        <div className="mem__edit">
          <textarea
            className="mem__input"
            rows={2}
            value={text}
            maxLength={1000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
            }}
            aria-label="Memory text"
          />
          <button type="button" className="agent-icon" onClick={save} disabled={busy} aria-label="Save"><Check size={13} aria-hidden="true" /></button>
        </div>
      ) : (
        <p className="mem__text">{item.text}</p>
      )}
      {!readOnly && !editing && (
        <div className="mem__actions">
          <button type="button" className="agent-icon" onClick={() => setEditing(true)} aria-label="Edit"><Pencil size={12} aria-hidden="true" /></button>
          {confirm ? (
            <>
              <button type="button" className="agent-btn is-bad" onClick={() => { setConfirm(false); onDelete?.(item.id); }}>Forget it</button>
              <button type="button" className="agent-btn" onClick={() => setConfirm(false)}>Keep</button>
            </>
          ) : (
            <button type="button" className="agent-icon" onClick={() => setConfirm(true)} aria-label="Delete"><Trash2 size={12} aria-hidden="true" /></button>
          )}
        </div>
      )}
    </li>
  );
}

export default function MemoryPanel({
  items = [], asset = null, assetLabel = (id) => id, flash = null, onAdd, onEdit, onDelete, onClose, readOnly = false, error = null,
}) {
  const [text, setText] = useState('');
  const flashKey = flash ? `${flash.id}|${flash.at}` : null;
  const [expiredFlash, setExpiredFlash] = useState(null);

  useEffect(() => {
    if (!flashKey) return undefined;
    const timer = setTimeout(() => setExpiredFlash(flashKey), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashKey]);
  const flashId = flashKey && flashKey !== expiredFlash ? flash.id : null;

  const ordered = [...items].sort((a, b) => {
    const openA = a.kind === 'open_item' && a.status === 'open' ? 0 : 1;
    const openB = b.kind === 'open_item' && b.status === 'open' ? 0 : 1;
    return openA - openB || String(b.created_at).localeCompare(String(a.created_at));
  });

  const add = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAdd?.(t);
    setText('');
  };

  return (
    <section className="agent-panel mem" aria-label="Memory">
      <header className="agent-panel__head">
        <div className="agent-panel__titles">
          <span className="agent-panel__kicker">Memory</span>
          <h2 className="agent-panel__title">{asset ? assetLabel(asset) : 'No asset'}</h2>
        </div>
        {onClose && <button type="button" className="agent-icon" onClick={onClose} aria-label="Close memory"><X size={14} aria-hidden="true" /></button>}
      </header>
      <div className="agent-panel__body">
        {error && <p className="mem__error" role="alert">{error}</p>}
        {ordered.length === 0 && <p className="mem__empty">Nothing remembered for this asset yet.</p>}
        <ul className="mem__list">
          {ordered.map((item) => (
            <MemoryItem key={item.id} item={item} flashing={flashId === item.id} readOnly={readOnly} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </ul>
      </div>
      {!readOnly && typeof onAdd === 'function' && (
        <form className="mem__add" onSubmit={add}>
          <input
            className="mem__input"
            type="text"
            value={text}
            maxLength={1000}
            placeholder="Add something to remember"
            aria-label="New memory"
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="agent-btn" disabled={!text.trim() || !asset}><Plus size={12} aria-hidden="true" /> Add</button>
        </form>
      )}
    </section>
  );
}
